import { useState, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { evaluateRun, getAchievementById } from "@/lib/achievements";
import { playSound } from "@/lib/sfx";
import { todayString } from "@/lib/gameEngine";
import { devError, devWarn } from "@/lib/devLog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const NAME_KEY = "aobe_last_name";

function persistName(name) {
  try {
    localStorage.setItem(NAME_KEY, name);
  } catch (e) {
    devWarn("useSubmitScore: localStorage write failed", e);
  }
}

function announceRank(rank, name) {
  if (rank === 1) toast.success(`#${rank} — YOU TOP THE BOARD`);
  else if (rank) toast.success(`SCORE SAVED — RANK #${rank}`);
  else toast.success(`Score saved for ${name.toUpperCase()}`);
}

function announceUnlocks(unlocked) {
  if (!unlocked.length) return;
  playSound("achievement");
  unlocked.forEach((id, i) => {
    const a = getAchievementById(id);
    if (!a) return;
    setTimeout(() => {
      toast(`UNLOCKED · ${a.label}`, { description: a.desc });
    }, i * 350);
  });
}

/**
 * Encapsulates the score-submission side effects: HTTP POST, name persistence,
 * rank toast, achievement evaluation + unlock announcements.
 */
export function useSubmitScore() {
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [rank, setRank] = useState(null);
  const [newAchievements, setNewAchievements] = useState([]);
  // Synchronous re-entrancy guard — protects against multiple .click()s in the
  // same JS tick before React has flushed the `submitting` state update.
  const inFlightRef = useRef(false);

  const submit = useCallback(async ({ name, runStats }) => {
    const cleanName = name.trim();
    if (!cleanName) {
      toast.error("Enter a name to save your score.");
      return;
    }
    if (inFlightRef.current || submitted) return;
    inFlightRef.current = true;
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/leaderboard`, {
        player_name: cleanName,
        score: runStats.score,
        distance: runStats.distance,
        coins: runStats.coins,
        enemies_defeated: runStats.enemiesDefeated,
        bosses_defeated: runStats.bossesDefeated,
        mode: runStats.mode,
        seed_date: runStats.mode === "daily" ? runStats.seedDate : null,
      });
      persistName(cleanName);
      const nextRank = res.data?.rank ?? null;
      setRank(nextRank);
      setSubmitted(true);
      announceRank(nextRank, cleanName);

      const unlocked = evaluateRun({
        score: runStats.score,
        coinsCollected: runStats.coins,
        bossesDefeated: runStats.bossesDefeated,
        powerupsByType: runStats.powerupsByType,
        mode: runStats.mode,
        seedDate: runStats.seedDate,
        todayString: todayString(),
      });
      if (unlocked.length) {
        setNewAchievements(unlocked);
        announceUnlocks(unlocked);
      }
    } catch (err) {
      toast.error("Could not save score. Try again.");
      devError("useSubmitScore: submit failed", err);
    } finally {
      setSubmitting(false);
      inFlightRef.current = false;
    }
  }, [submitted]);

  return { submit, submitting, submitted, rank, newAchievements };
}
